/**
 * Date input utilities — auto-formatting, parsing, and timezone-safe display
 * for MM/DD/YYYY date inputs.
 * 
 * TIMEZONE STRATEGY:
 * - Dates without time (acquisition date, hire date, etc.) are stored as plain
 *   "YYYY-MM-DD" strings — no timezone, no ISO timestamp. May 5th is May 5th everywhere.
 * - When displaying, ALWAYS use splitDateString() or formatDateForDisplay() — never
 *   pass a date-only string through `new Date()` without appending 'T00:00:00'.
 * - For "today's date", use getLocalDateString() instead of new Date().toISOString().
 * - Only use ISO timestamps (with Z suffix) for fields that represent a specific moment
 *   in time (createdAt, inspectionCompleted, etc.).
 */

/**
 * Auto-formats a date input string as the user types.
 * Strips non-digit characters, inserts "/" after MM and DD, and validates
 * digit ranges as you type (month 01-12, day 01-31).
 * 
 * @param rawInput - The current input value
 * @param previousValue - The previous input value (used to detect backspace)
 */
export function autoFormatDateInput(rawInput: string, previousValue: string = ''): string {
    // If user is deleting (input is shorter than previous), don't auto-format
    if (rawInput.length < previousValue.length) {
        return rawInput;
    }

    // Strip everything except digits
    const digits = rawInput.replace(/\D/g, '');

    // Limit to 8 digits (MMDDYYYY)
    const limited = digits.slice(0, 8);

    // Validate and build formatted string with position-aware constraints
    let formatted = '';
    for (let i = 0; i < limited.length; i++) {
        const digit = limited[i];

        // Position 0: month tens digit — only 0 or 1
        if (i === 0 && parseInt(digit) > 1) {
            formatted += '0' + '/'; // auto-prefix with 0 (e.g., typing "5" → "05/")
            formatted += digit;
            continue;
        }
        // Position 1: month ones digit — if tens is 1, max is 2 (for month 12)
        if (i === 1) {
            const tens = parseInt(limited[0]);
            if (tens === 1 && parseInt(digit) > 2) continue; // skip invalid
        }
        // Position 2: day tens digit — only 0, 1, 2, or 3
        if (i === 2 && parseInt(digit) > 3) {
            formatted += '/';
            formatted += '0'; // auto-prefix with 0
            formatted += digit;
            continue;
        }
        // Position 3: day ones digit — if tens is 3, max is 1 (for day 31)
        if (i === 3) {
            const dayTens = parseInt(limited[2]);
            if (dayTens === 3 && parseInt(digit) > 1) continue; // skip invalid
        }
        // Position 4: year thousands digit — only 1 or 2
        if (i === 4 && parseInt(digit) !== 1 && parseInt(digit) !== 2) continue;

        // Insert slashes at the right positions
        if (i === 2 || i === 4) {
            formatted += '/';
        }
        formatted += digit;
    }

    return formatted;
}

/**
 * Parses a date input string (MM/DD/YYYY or MMDDYYYY) into a timezone-safe
 * date string "YYYY-MM-DD". Returns empty string if invalid.
 * 
 * Accepts:
 *   - "05/04/2005" (with slashes)
 *   - "05-04-2005" (with dashes)
 *   - "05042005"   (raw 8 digits)
 */
export function parseDateInput(input: string): string {
    // Try MM/DD/YYYY or MM-DD-YYYY
    const slashMatch = input.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (slashMatch) {
        const [, month, day, year] = slashMatch;
        return validateAndFormat(month, day, year);
    }

    // Try raw 8 digits: MMDDYYYY
    const rawMatch = input.match(/^(\d{2})(\d{2})(\d{4})$/);
    if (rawMatch) {
        const [, month, day, year] = rawMatch;
        return validateAndFormat(month, day, year);
    }

    return '';
}

/**
 * Formats a stored date string ("YYYY-MM-DD") for display as "MM/DD/YYYY".
 * Timezone-safe — does NOT use new Date() constructor.
 */
export function formatStoredDateForDisplay(storedDate: string | null | undefined): string {
    if (!storedDate) return '-';

    // Handle "YYYY-MM-DD" format directly (no Date constructor needed)
    const isoMatch = storedDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return `${month}/${day}/${year}`;
    }

    // Handle ISO timestamp "2025-05-04T..." — extract just the date part
    if (storedDate.includes('T')) {
        const datePart = storedDate.split('T')[0];
        const parts = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (parts) {
            const [, year, month, day] = parts;
            return `${month}/${day}/${year}`;
        }
    }

    // Handle "MM/DD/YYYY" already formatted
    if (storedDate.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        return storedDate;
    }

    return storedDate;
}

/**
 * Gets today's date as a timezone-safe "YYYY-MM-DD" string.
 * Uses local time (not UTC), so "today" is always the user's local date.
 */
export function getLocalDateString(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export interface LocalDayRange {
    /** Local midnight converted to UTC ISO string for GraphQL filters */
    dateStart: string;
    /** Local 23:59:59.999 converted to UTC ISO string for GraphQL filters */
    dateEnd: string;
}

/**
 * Returns the browser-local calendar day as a UTC ISO range (midnight → end of day).
 */
export function getLocalDayRange(referenceDate: Date = new Date()): LocalDayRange {
    const start = new Date(referenceDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(referenceDate);
    end.setHours(23, 59, 59, 999);

    return {
        dateStart: start.toISOString(),
        dateEnd: end.toISOString(),
    };
}

/** True when an ISO timestamp falls within a local-day range from getLocalDayRange. */
export function isTimestampInLocalDayRange(
    isoTimestamp: string | null | undefined,
    range: LocalDayRange,
): boolean {
    if (!isoTimestamp) {
        return false;
    }

    const time = new Date(isoTimestamp).getTime();
    if (Number.isNaN(time)) {
        return false;
    }

    return time >= new Date(range.dateStart).getTime() && time <= new Date(range.dateEnd).getTime();
}

/**
 * Converts a stored "YYYY-MM-DD" string to a Date object WITHOUT timezone shift.
 * Use this whenever you need a Date object from a date-only string
 * (e.g., for calendar pickers, comparisons).
 */
export function dateFromStoredString(stored: string): Date {
    // Append T00:00:00 to prevent UTC interpretation
    return new Date(stored + 'T00:00:00');
}

/** Minimum selectable year for year-only DateField mode. */
export const YEAR_PICKER_MIN = 1990;

/** Upper bound for year-only pickers (defaults to current calendar year). */
export const getYearPickerMax = (referenceDate: Date = new Date()): number => referenceDate.getFullYear();

/**
 * Display text for a stored year value.
 * Accepts `YYYY` or legacy `YYYY-MM-DD` and returns the year portion.
 */
export function formatStoredYearForDisplay(storedValue: string | null | undefined): string {
    if (!storedValue) return '';

    const trimmed = storedValue.trim();
    if (/^\d{4}$/.test(trimmed)) {
        return trimmed;
    }

    const isoMatch = trimmed.match(/^(\d{4})-\d{2}-\d{2}/);
    if (isoMatch) {
        return isoMatch[1];
    }

    return '';
}

/** Restrict year input to digits and max length while typing. */
export function autoFormatYearInput(rawInput: string, previousValue: string = ''): string {
    if (rawInput.length < previousValue.length) {
        return rawInput.replace(/\D/g, '').slice(0, 4);
    }

    return rawInput.replace(/\D/g, '').slice(0, 4);
}

/**
 * Parse a year input into stored `YYYY` format.
 * Returns empty string when incomplete or outside the allowed range.
 */
export function parseYearInput(
    input: string,
    minYear: number = YEAR_PICKER_MIN,
    maxYear: number = getYearPickerMax(),
): string {
    const digits = input.replace(/\D/g, '');
    if (digits.length !== 4) return '';

    const year = parseInt(digits, 10);
    if (year < minYear || year > maxYear) return '';

    return String(year);
}

/** Build descending year options for year-only pickers. */
export function buildYearPickerOptions(
    minYear: number = YEAR_PICKER_MIN,
    maxYear: number = getYearPickerMax(),
): number[] {
    const years: number[] = [];
    for (let year = maxYear; year >= minYear; year -= 1) {
        years.push(year);
    }
    return years;
}

/** Normalize stored year values (`YYYY` or legacy `YYYY-MM-DD`) to canonical `YYYY`. */
export function normalizeStoredYearValue(
    storedValue: string | null | undefined,
    minYear: number = YEAR_PICKER_MIN,
    maxYear: number = getYearPickerMax(),
): string {
    const displayYear = formatStoredYearForDisplay(storedValue);
    if (!displayYear) return '';
    return parseYearInput(displayYear, minYear, maxYear);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function validateAndFormat(month: string, day: string, year: string): string {
    const m = parseInt(month);
    const d = parseInt(day);
    const y = parseInt(year);

    if (m < 1 || m > 12) return '';
    if (d < 1 || d > 31) return '';
    if (y < 1900 || y > 2100) return '';

    // Verify it's a real date (using local time to avoid timezone issues)
    const date = new Date(y, m - 1, d);
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
        return '';
    }

    return `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}


// ----- Calendar date helpers (pure, no Amplify) -----

/** YYYY-MM-DD in the user's local timezone (avoids UTC day-shift from toISOString). */
export const toLocalCalendarDateString = (date: Date = new Date()): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Calendar day from a stored date-only value or an ISO timestamp.
 * Date-only strings are kept as written; timestamps convert to local YYYY-MM-DD.
 */
export const toCalendarDateString = (value: string | null | undefined): string | null => {
    if (!value) return null;
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const slashMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashMatch) {
        return `${slashMatch[3]}-${slashMatch[1]}-${slashMatch[2]}`;
    }
    const parsed = new Date(trimmed);
    if (isNaN(parsed.getTime())) return null;
    return toLocalCalendarDateString(parsed);
};
